import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// Dynamically load all images from src/assets/img
const projectImages = {};

try {
    const context = require.context('../assets/img', true, /\.(png|jpe?g|svg|webp)$/);
    context.keys().forEach((key) => {
        const parts = key.split('/');
        if (parts.length >= 3) {
            const folderName = parts[1];
            if (!projectImages[folderName]) {
                projectImages[folderName] = [];
            }
            projectImages[folderName].push(context(key));
        }
    });
} catch (error) {
    console.error("Failed to load project images:", error);
}

const ProjectGallery = ({ open, onClose, folder, title }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const images = projectImages[folder] || [];

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft') handlePrev();
        if (e.key === 'ArrowRight') handleNext();
        if (e.key === 'Escape') onClose();
    };

    // Reset index when dialog opens
    React.useEffect(() => {
        if (open) setCurrentIndex(0);
    }, [open]);

    if (images.length === 0) {
        return (
            <Dialog fullScreen open={open} onClose={onClose}>
                <DialogContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <Typography>No images found for this project.</Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            onKeyDown={handleKeyDown}
            PaperProps={{
                sx: {
                    backgroundColor: "#fff",
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 3,
                    py: 1.5,
                    borderBottom: "1px solid #eee",
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="h6"
                    fontFamily="Audrey"
                    sx={{ textTransform: "uppercase", letterSpacing: "0.1rem" }}
                >
                    {title}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                        variant="body2"
                        fontFamily="Avenir"
                        color="text.secondary"
                    >
                        {currentIndex + 1} / {images.length}
                    </Typography>
                    <IconButton onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Main Image Area */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                    minHeight: 0,
                    px: { xs: 1, md: 6 },
                    py: 2,
                }}
            >
                {/* Prev Button */}
                <IconButton
                    onClick={handlePrev}
                    sx={{
                        position: "absolute",
                        left: { xs: 4, md: 16 },
                        zIndex: 2,
                        backgroundColor: "rgba(255,255,255,0.8)",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.95)",
                            boxShadow: 2,
                        },
                    }}
                >
                    <ArrowBackIosNewIcon />
                </IconButton>

                {/* Current Image */}
                <Box
                    component="img"
                    src={images[currentIndex]}
                    alt={`${title} - ${currentIndex + 1}`}
                    sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        borderRadius: "4px",
                        userSelect: "none",
                    }}
                />

                {/* Next Button */}
                <IconButton
                    onClick={handleNext}
                    sx={{
                        position: "absolute",
                        right: { xs: 4, md: 16 },
                        zIndex: 2,
                        backgroundColor: "rgba(255,255,255,0.8)",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.95)",
                            boxShadow: 2,
                        },
                    }}
                >
                    <ArrowForwardIosIcon />
                </IconButton>
            </Box>

            {/* Thumbnail Strip */}
            <Box
                sx={{
                    flexShrink: 0,
                    borderTop: "1px solid #eee",
                    px: 2,
                    py: 1.5,
                    overflowX: "auto",
                    display: "flex",
                    gap: 1,
                    justifyContent: "center",
                    scrollbarWidth: "none",
                    "-ms-overflow-style": "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {images.map((img, idx) => (
                    <Box
                        key={idx}
                        component="img"
                        src={img}
                        alt={`Thumbnail ${idx + 1}`}
                        onClick={() => setCurrentIndex(idx)}
                        sx={{
                            width: { xs: 50, sm: 64, md: 72 },
                            height: { xs: 50, sm: 64, md: 72 },
                            objectFit: "cover",
                            borderRadius: "4px",
                            cursor: "pointer",
                            flexShrink: 0,
                            border: currentIndex === idx
                                ? "2px solid #333"
                                : "2px solid transparent",
                            opacity: currentIndex === idx ? 1 : 0.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                                opacity: 1,
                            },
                        }}
                    />
                ))}
            </Box>
        </Dialog>
    );
};

export default ProjectGallery;
